import {v4 as uuidv4} from 'uuid';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState, {
  UseGlobalClientStateOutput,
} from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {assertNever} from 'util/Types';

import type {ChatDraftState} from '../ChatDraftState';
import {setDraftState} from '../ChatDraftState';
import {AISettingsTab} from './AISettings';
import type {SidepanelChat} from './ChatRouter';

const AI_PANEL_STATE_KEY = 'ai-panel-state-v2';

/**
 * Indicates where to route within the AI sidepanel.
 *
 * Enum values are directly encoded in the URL shared in marketing emails
 * (see AIRoutes.openDraftChatFromQueryString), and therefore should not be changed.
 * The query string variable "modal" is a misnomer.
 */
export enum InitialUI {
  SIGNUP = 'signup',
  SETTINGS = 'settings',
}

export enum InitialModal {
  SIGNUP_MODAL = 'SIGNUP_MODAL',
}

export interface AIPanelState {
  /**
   * A client-local ID used as the key for the ChatRouter component. This allows us to ensure that
   * state does not leak between chat sessions when rendering ChatRouter. This is simply not the chat
   * ID since that does not exist when a new chat is started.
   */
  clientChatId: string;

  /**
   * The chat session that is currently active.
   */
  activeChat: SidepanelChat;

  // Whether the history is shown.
  showHistory: boolean;

  // The user's preference for whether history is shown.
  showHistoryPreference: boolean;

  initialModal: InitialModal | undefined;

  activeSettingsTab: AISettingsTab | null;

  // Whether the user has clicked "Start" on the onboarding banner, bypassing it to show the full
  // onboarding screen directly. Persisted in global state so navigation away and back doesn't reset it.
  showOnboardingScreen: boolean;
}

export const panelStateHook = makeUseGlobalClientState<AIPanelState>({
  activeChat: {
    id: null,
  },
  showHistory: false,
  showHistoryPreference: false,
  clientChatId: uuidv4(),
  initialModal: undefined,
  activeSettingsTab: null,
  showOnboardingScreen: false,
});

export function useAISidepanelGlobalClientState(): UseGlobalClientStateOutput<AIPanelState> {
  return panelStateHook.useGlobalClientState(AI_PANEL_STATE_KEY);
}

export function getAISidepanelState(): AIPanelState {
  return panelStateHook.getState(AI_PANEL_STATE_KEY);
}

export function openExistingChat(chatSessionId: string): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: null},
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
  });
}

/** Like openExistingChat, but preserves a known chat type so ChatRouter can render immediately. */
export function remountExistingChatWithType(chatSessionId: string, chatType: ChatType): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: chatType},
    showHistoryPreference: false,
    showHistory: false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
  });
}

export function openDraftChat(draftState: ChatDraftState, options: {initialUI?: InitialUI} = {}): void {
  setDraftState(null, draftState);

  let initialUIState: Partial<AIPanelState> = {};
  if (options.initialUI != null) {
    switch (options.initialUI) {
      case InitialUI.SIGNUP:
        initialUIState = {initialModal: InitialModal.SIGNUP_MODAL};
        break;
      case InitialUI.SETTINGS:
        initialUIState = {activeSettingsTab: AISettingsTab.GENERAL};
        break;
      default:
        assertNever(options.initialUI);
    }
  }

  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: null},
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: undefined,
    activeSettingsTab: null,
    showOnboardingScreen: false,
    ...initialUIState,
  });
}

export function openSettings(tab?: AISettingsTab): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    ...getAISidepanelState(),
    activeSettingsTab: tab ?? AISettingsTab.GENERAL,
  });
}

export function getActiveChatId(): string | null {
  return getAISidepanelState().activeChat.id;
}
